<template>
  <section
    class="stores-form"
    aria-label="Für Cashback registrieren"
  >
    <GridContainer padding>
      <div class="stores-form__head">
        <p class="stores-form__eyebrow">So funktioniert es</p>
        <Heading
          tag="h2"
          size="lg"
          align="center"
          spaces-bottom-rem
        >
          In drei Schritten zum <span class="text--primary">Cashback.</span>
        </Heading>
        <Paragraph
          align="center"
          class="stores-form__paragraph"
        >
          Erst registrieren, dann die Handyrechnung im Store hinterlegen und anschließend kaufen.
          Für deinen ersten Kauf gibt es bis zu 10&nbsp;€ zurück.
        </Paragraph>
      </div>

      <Card class="stores-form__form-container">
        <a
          id="dcb-mailform"
          style="position: absolute; top: -72px"
        />
        <SquareBadge
          theme="magenta"
          class="stores-form__badge"
          text="#1"
        />
        <div class="text--bold text--mb text--uppercase text--primary stores-form__text-step">
          Schritt 1 von 3
        </div>
        <Heading
          size="sm"
          tag="h2"
          spaces-bottom-rem
          class="stores-form__heading"
        >
          Für Cashback registrieren
        </Heading>
        <Paragraph class="stores-form__content">
          Mit deiner Telekom Mobilfunknummer aktivierst du den Cashback-Anspruch. Gültig für Telekom
          Kunden mit Laufzeitvertrag.
        </Paragraph>

        <form
          class="stores-form__form"
          :class="{ 'stores-form__form--error': responseErrors.length > 0 }"
          @submit.prevent="submit"
        >
          <TextInput
            id="rd-email"
            v-model="mail"
            type="email"
            has-fixed-value
            size="medium"
            label="E-Mail-Adresse *"
            placeholder="name@beispiel.de"
            :error-message="visibleErrors.mail"
            class="stores-form__input-email"
          />

          <div class="stores-form__phone">
            <TextInput
              id="rd-phone-area"
              v-model="phoneArea"
              type="tel"
              has-fixed-value
              size="medium"
              label="Vorwahl *"
              placeholder="0151"
              :maxlength="5"
              :error-message="visibleErrors.phoneArea"
              class="stores-form__phone-area"
            />
            <TextInput
              id="rd-phone-number"
              v-model="phoneNumber"
              type="tel"
              has-fixed-value
              size="medium"
              label="Rufnummer *"
              placeholder="1234567"
              :error-message="visibleErrors.phoneNumber"
              class="stores-form__phone-number"
            />
          </div>
          <div class="stores-form__phone-info">
            Deine Telekom Mobilfunknummer. Darüber ordnen wir dir den Cashback zu.
          </div>

          <div class="stores-form__legal">
            <CheckboxInput
              id="agb"
              v-model="agb"
              :error-message="visibleErrors.agb"
              class="stores-form__checkbox"
              required
            >
              Ja, ich akzeptiere die
              <a
                href="/downloads/2608_Teilnahmebedingungen-und-Datenschutzhinweise_Zahl-einfach-per-Handyrechnung_AppStreaming.pdf"
                target="_blank"
                class="link"
                style="display: inline"
              >
                Teilnahmebedingungen und Datenschutzhinweise </a
              >.
            </CheckboxInput>

            <CheckboxInput
              id="reminder"
              v-model="reminder"
              class="stores-form__checkbox"
            >
              Ja, ich möchte zukünftig über Neuigkeiten und Marketingaktionen zu „Zahl einfach per
              Handyrechnung“ durch die i-Sell GmbH, Erlanger Str. 9-13, 91083 Baiersdorf persönlich
              informiert und beraten werden und stimme der Kontaktaufnahme per E-Mail, SMS und über
              andere elektronische Messengerdienste zu. Es gelten die
              <a
                href="/downloads/Optin-DCB.pdf"
                target="_blank"
                class="link"
                style="display: inline"
              >
                Datenschutzhinweise der i-Sell GmbH </a
              >.
            </CheckboxInput>
          </div>

          <div
            v-if="responseErrors.length > 0"
            class="stores-form__errors"
          >
            <div
              v-for="(msg, key) in responseErrors"
              :key="key"
              class="error-message"
              v-html="msg"
            />
          </div>

          <div class="stores-form__button">
            <LinkButton
              tag="button"
              variant="primary"
              size="medium"
              is-wide
              :disabled="step === 'mailSent'"
              tealium-name="dcb.stores.form.submit"
              @click.prevent="submit"
            >
              Cashback aktivieren
            </LinkButton>
            <p class="stores-form__required">* Pflichtfeld</p>
          </div>

          <div
            v-if="step === 'mailSent'"
            class="text--primary text--h4 text--bold"
          >
            Registrierung erledigt. Jetzt richtest du unten die Handyrechnung in deinem Store ein.
          </div>
        </form>

        <div class="stores-form__trust">
          <span>
            <svg
              class="stores-form__trust-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.4"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Offizieller Telekom Service, SSL-verschlüsselt
          </span>
          <span>
            <svg
              class="stores-form__trust-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.4"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Keine Kartendaten, jederzeit per Drittanbietersperre steuerbar
          </span>
        </div>
      </Card>
    </GridContainer>
  </section>
</template>

<script setup lang="ts">
import {
  Card,
  TextInput,
  CheckboxInput,
  LinkButton,
  GridContainer,
  Heading,
  Paragraph,
  SquareBadge,
} from '@i22team/td-longtail-vue-components';
import { useMailForm } from '~/lib/dcb/dcb-mailform';
import type { DcbStep } from '~/lib/dcb/dcb-double-opt-in';

export type MailFormProps = {
  step?: DcbStep;
};

const props = withDefaults(defineProps<MailFormProps>(), {
  step: undefined,
});

const emits = defineEmits(['mailSent', 'showError']);

const {
  mail,
  mail2,
  phoneArea,
  phoneNumber,
  agb,
  reminder,
  submit,
  visibleErrors,
  responseErrors,
} = useMailForm({
  emits,
  dcbOptions: {
    identifier: 'stores',
    path: '/handyrechnung-stores',
  },
  hideFormCallback: () => {
    responseErrors.value.push('Du bist bereits registriert');
  },
  specialFields: {
    reminder: { required: false },
  },
});

watch(mail, (v) => (mail2.value = v));

watch(
  () => props.step,
  () => {
    if (props.step === 'mailSent') {
      setTimeout(() => {
        document.getElementById('wofuer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 1500);
    }
  }
);
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.stores-form {
  padding-block: 3rem;

  @include mq($mq-md) {
    padding-block: 4rem;
  }

  &__eyebrow {
    font-size: 15px;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--color-primary);
    margin-bottom: 12px;
    text-align: center;
  }

  &__head {
    max-width: 720px;
    margin-inline: auto;
    text-align: center;
    margin-bottom: 2rem;
  }

  &__paragraph {
    font-size: 1.2rem;
  }

  &__badge {
    --square-badge-radius: 0 var(--card-radius);
    position: absolute;
    right: 0;
    top: 0;
    padding: 0.7rem;
  }

  &__form-container {
    --card-bg-color: #f9f9f9;
    --card-padding: 32px;
    --color-bg: #fff;
    max-width: 600px;
    margin-inline: auto;
    border: 1px solid #e0e0e0;
    position: relative;
    font-size: 16px;
    box-shadow: 0 18px 50px rgba(16, 12, 24, 0.09);
  }

  &__text-step {
    font-size: 1.1rem;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  &__phone {
    display: flex;
    align-items: baseline;
    gap: 8px;

    &-number {
      width: 100%;
    }
  }

  &__phone-info {
    font-size: 14px;
    margin-top: -1rem;
  }
  :deep(.input-wrapper__input) {
    &::placeholder {
      color: var(--color-gray-500);
    }
  }

  &__legal {
    display: flex;
    flex-direction: column;
    gap: $grid-base;
  }

  &__checkbox {
    --color-font: #000;
    font-size: 15px;

    :deep(input) {
      right: 1px;
    }
  }

  &__errors {
    color: var(--color-error);
  }

  &__button {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__required {
    font-size: 13px;
    color: var(--color-font-subtle);
    margin: 0;
  }

  &__trust {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #f2f2f2;
    font-size: 16px;
    color: #666;
  }

  &__trust-icon {
    color: var(--color-primary);
    margin-right: 4px;
    width: 16px;
    vertical-align: middle;

    > svg {
      width: 16px;
      vertical-align: middle;
    }
  }
}
</style>
